<template>
  <div
    class="card h-full bg-surface shadow-xl relative"
    data-testid="show-item-card"
    :class="{
      'w-[312px] max-w-full min-h-[240px]': !variant || variant === 'regular',
      'w-full ': variant === 'responsive',
      'rounded-3xl': !channelStore.isAsMonacoFanToken,
      'rounded-none': channelStore.isAsMonacoFanToken,
    }"
  >
    <RouterLink
      :to="{
        name: RouteNames.Show,
        params: {
          channel: show.channel_id ?? show.channel.id,
          show: show.id,
        },
      }"
      @click="emit('click')"
    >
      <div class="relative">
        <figure
          class="aspect-[230_/_150] bg-fill-brand-hover"
          :class="{ 'rounded-t-3xl': !channelStore.isAsMonacoFanToken }"
        >
          <img
            :src="cardImage"
            :width="!show.thumbnail ? 80 : undefined"
            :height="!show.thumbnail ? 80 : undefined"
            :alt="show.title"
            class="w-full h-full object-cover"
          />
        </figure>

        <div
          v-if="!channelStore.isLeaderboardDisabled"
          class="absolute right-3.5 top-3.5"
        >
          <PointScoreBadge :coins="pointsForShow" class="w-8 h-8" />
        </div>
      </div>
    </RouterLink>

    <!-- Card body -->
    <RouterLink
      :to="{
        name: RouteNames.Show,
        params: {
          channel: show.channel_id ?? show.channel.id,
          show: show.id,
        },
      }"
      class="card-body p-4 gap-1 flex flex-col items-start justify-between shadow-3xl bg-surface hover:bg-surface-hover"
      :class="{ 'rounded-b-3xl': !channelStore.isAsMonacoFanToken }"
      @click="emit('click')"
    >
      <div class="w-full flex items-start justify-between">
        <div
          class="h-[68px] flex-col justify-start items-start gap-1 inline-flex"
        >
          <h2 class="self-stretch font-semibold font-sans line-clamp-2">
            {{ title }}
          </h2>
          <div
            class="text-white/80 text-xs font-medium font-sans leading-[18px]"
          >
            {{ scheduledStartTime }}
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center gap-2 w-full">
        <Counter
          v-if="totalSecondsLeft > 0 && show.status === ShowStatus.Scheduled"
          :data="formattedTimeLeft"
          :priority="CardPriority.Normal"
        />
        <div
          v-if="
            show.status === ShowStatus.Live ||
            (show.status === ShowStatus.Scheduled && totalSecondsLeft <= 0)
          "
          class="h-[89px] pt-1.5 pb-4 justify-center items-center gap-2 inline-flex"
        >
          <div
            class="text-center text-white text-xl font-normal font-serif uppercase"
          >
            {{ $t("channel.shows.actions.joinNow") }}
          </div>
        </div>
        <div
          v-if="show.status === ShowStatus.Ended"
          class="h-[89px] pt-1.5 pb-4 justify-center items-center gap-2 inline-flex"
        >
          <div
            class="text-center text-white text-xl font-normal font-serif uppercase"
          >
            {{ $t("channel.shows.actions.watchReplay") }}
          </div>
        </div>
        <div
          v-if="show.status === ShowStatus.VideoCast"
          class="h-[89px] pt-1.5 pb-4 justify-center items-center gap-2 inline-flex"
        >
          <div
            class="text-center text-white text-xl font-normal font-serif uppercase"
          >
            {{ $t("channel.shows.actions.watchNow") }}
          </div>
        </div>
      </div>
    </RouterLink>

    <!-- User section -->
    <div
      class="h-16 p-4 justify-between items-center inline-flex w-full shadow-2xl"
      :class="{ 'rounded-b-3xl': !channelStore.isAsMonacoFanToken }"
    >
      <div
        class="w-full grow shrink basis-0 h-8 justify-between items-start flex"
      >
        <div class="flex-1 justify-start items-center gap-2 flex">
          <Avatar size="sm" :user="show.created_by ?? undefined" />
          <div
            class="text-white text-xs font-medium font-sans leading-[18px] line-clamp-1"
          >
            {{
              $t("channel.shows.creator.by", {
                username: show.created_by?.username,
              })
            }}
          </div>
        </div>

        <div class="flex flex-row gap-2 shrink-0">
          <IconButtonV2 icon="mdi:ios-share" @click="onShareClick" />

          <ShowItemDropdown
            :show="show"
            :is-pinned="isPinned"
            @toggle-pin="onTogglePinClick"
          />
        </div>
      </div>
    </div>

    <div class="absolute top-4 left-4 flex flex-row gap-1.5">
      <!-- Statuses -->
      <div
        v-if="show.status === ShowStatus.Live"
        class="bg-fill-critical text-text px-2 py-1.5 rounded-xl flex gap-2 items-center justify-center text-sm"
      >
        <div class="animate-ping rounded-full font-extrabold">&middot;</div>
        <span>{{ $t("channel.shows.status.live") }}</span>
      </div>

      <div
        v-if="show.status === ShowStatus.Scheduled"
        class="bg-fill-brand text-text px-2 py-1.5 rounded-xl flex gap-2 items-center justify-center text-sm"
      >
        <div class="animate-ping rounded-full font-extrabold">&middot;</div>
        <span>{{ $t("channel.shows.status.upcoming") }}</span>
      </div>

      <div
        v-if="show.status === ShowStatus.Ended"
        class="bg-fill-brand text-text px-2 py-1.5 rounded-xl flex gap-2 items-center justify-center text-sm"
      >
        <span>{{ $t("channel.shows.status.replay") }}</span>
      </div>

      <div
        v-if="show.status === ShowStatus.VideoCast"
        class="bg-fill-dark border-2 border-fill-brand text-text px-2 py-1.5 rounded-xl flex gap-2 items-center justify-center text-sm"
      >
        <span>{{ $t("channel.shows.status.video") }}</span>
      </div>

      <div
        v-if="isPinned"
        class="pointer-events-none aspect-square p-1.5 rounded-xl bg-black bg-opacity-15 backdrop-blur-lg"
      >
        <Icon icon="ix:pin-filled" class="fill-basics-primary size-5" />
      </div>
    </div>

    <Spinner v-if="togglePinLoading" />
  </div>
</template>

<script setup lang="ts">
import {
  ExperienceType,
  PinShowToChannelDocument,
  type Show,
  ShowStatus,
  UnpinShowFromChannelDocument,
} from "@/generated/graphql";
import Avatar from "@/components/me/avatar.vue";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import ShowItemDropdown from "./show-item-dropdown.vue";
import PointScoreBadge from "../contests/point-score-badge.vue";
import { graphql as gql } from "@/generated";
import { useQuery } from "@vue/apollo-composable";
import { computed, onUnmounted } from "vue";
import Counter from "@/components/channel/contests/countdown-display.vue";
import { useCountdown } from "@/composables/use-countdown";
import { CardPriority } from "@/components/channel/contests/creation/contests";
import { useShareReferral } from "@/composables/use-share-referral";
import IconButtonV2 from "@/components/ui/button/icon-button-v2.vue";
import { useObjectTranslation } from "@/stores/translation.store";
import { useUserStore } from "@/stores/user.store";
import { formatHumanReadableDate } from "@/utils/date";
import { Icon } from "@iconify/vue";
import { useMutation } from "@urql/vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { useI18n } from "vue-i18n";
import { useToastStore } from "@/stores/toast.store";
import Spinner from "@/components/ui/spinner.vue";

const GetPointsForJoiningShow = gql(/* GraphQL */ `
  query GetPointsForJoiningShow {
    getPointsForJoiningShow
  }
`);

const { result: result } = useQuery(GetPointsForJoiningShow);
const pointsForShow = computed(() =>
  String(result.value?.getPointsForJoiningShow ?? "..."),
);

const emit = defineEmits<{
  click: [];
  pinChanged: [];
}>();
const props = defineProps<{
  show: Show;
  variant?: "responsive" | "regular";
  isPinned?: boolean;
}>();

const channelStore = useChannelStore();
const userStore = useUserStore();

const scheduledStartTime = computed(() => {
  const ts = Number(props.show.scheduled_start_time);

  if (!isNaN(ts)) {
    const date = new Date(Number(ts));
    return formatHumanReadableDate(date, userStore.userLocale);
  }

  return "";
});

const { text: title } = useObjectTranslation(
  computed(() => ({
    authorUserId: props.show.created_by_id ?? props.show.created_by?.id,
    originalText: props.show.title,
    objectAccessors: { objectId: props.show.id, objectProperty: "title" },
    translatedText: props.show.show_translations[0]?.title,
  })),
);
const { text: description } = useObjectTranslation(
  computed(() => ({
    authorUserId: props.show.created_by_id ?? props.show.created_by?.id,
    originalText: props.show.description,
    objectAccessors: { objectId: props.show.id, objectProperty: "description" },
    translatedText: props.show.show_translations[0]?.description,
  })),
);

const { timeLeft, clearCountdown } = useCountdown(
  () => props.show?.scheduled_start_time as string,
);

const formattedTimeLeft = computed(() => ({
  days: timeLeft.days.value,
  hours: timeLeft.hours.value,
  minutes: timeLeft.minutes.value,
  seconds: timeLeft.seconds.value,
}));

const totalSecondsLeft = computed(() => {
  return (
    timeLeft.days.value * 86400 +
    timeLeft.hours.value * 3600 +
    timeLeft.minutes.value * 60 +
    timeLeft.seconds.value
  );
});

const onShareClick = useShareReferral({
  openGraphObject: computed(() => ({
    title: title.value,
    description: description.value,
    image: props.show.thumbnail,
  })),
  referralObject: computed(() => ({
    title: title.value ?? undefined,
    payload: {
      experience_id: props.show.id,
      experience_type: ExperienceType.Show,
      community_id:
        props.show.show_communities?.length === 1
          ? props.show.show_communities.at(0)?.community.id
          : undefined,
    },
  })),
});

const cardImage = computed(() => {
  return (
    props.show.thumbnail ||
    props.show.channel.logo ||
    channelStore.channel?.logo ||
    ""
  );
});

const { t } = useI18n();
const { addToast } = useToastStore();

const { executeMutation: pinShow } = useMutation(PinShowToChannelDocument);
const { executeMutation: unpinShow } = useMutation(
  UnpinShowFromChannelDocument,
);

const { handler: onTogglePinClick, loading: togglePinLoading } = useAwaitAction(
  async () => {
    try {
      if (props.isPinned) {
        const result = await unpinShow({ show_id: props.show.id });
        if (result?.data?.unpinShowFromChannel) {
          channelStore.removePinnedShowId(props.show.id);
          emit("pinChanged");
        } else {
          addToast({
            message: t("channel.shows.dropdown.unpinError"),
            type: "error",
          });
        }
      } else {
        const result = await pinShow({ show_id: props.show.id });
        if (result?.data?.pinShowToChannel) {
          channelStore.addPinnedShowId(props.show.id);
          emit("pinChanged");
        } else {
          addToast({
            message: t("channel.shows.dropdown.pinError"),
            type: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error toggling pin status:", error);
      addToast({
        message: props.isPinned
          ? t("channel.shows.dropdown.unpinError")
          : t("channel.shows.dropdown.pinError"),
        type: "error",
      });
    }
  },
);

onUnmounted(() => {
  clearCountdown();
});
</script>
